import dayjs from 'dayjs';
import { isCurrentUserToken } from '@lens/shared';
import { OPERATOR_LABELS } from '../components/FilterRow';
import { DATE_FORMAT } from '../lib/dateValue';
import type { FieldLike, FilterLeaf } from './types';

/**
 * Человекочитаемая сводка набора условий — «Статус = Новая · И · Оценка не пусто».
 * Нужна там, где показать редактор целиком дорого: список быстрых фильтров,
 * подсказки, свёрнутые состояния. Значения резолвятся в подписи (label опции
 * select, имя записи справочника через `resolvedRefs`), сырые id не показываем.
 */

function labelForValue(field: FieldLike | undefined, value: unknown, resolvedRefs?: Record<string, string>): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) {
    return value.map((v) => labelForValue(field, v, resolvedRefs)).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  // Подстановка «текущий пользователь» — показываем словом, а не токеном.
  if (isCurrentUserToken(value)) return 'я';
  const raw = String(value);
  if (!field) return raw;
  if (field.fieldType === 'select' || field.fieldType === 'multi_select') {
    const opts = (field.config as { options?: Array<string | { value: string; label?: string }> } | null)?.options ?? [];
    for (const o of opts) {
      if (typeof o === 'string') {
        if (o === raw) return o;
      } else if (o.value === raw) {
        return o.label || o.value;
      }
    }
    return raw;
  }
  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    return resolvedRefs?.[raw] ?? raw;
  }
  if (field.fieldType === 'date') {
    const d = dayjs(raw);
    return d.isValid() ? d.format(DATE_FORMAT) : raw;
  }
  return raw;
}

/** Одно условие текстом: «Поле оператор значение». */
export function describeFilterLeaf(
  leaf: FilterLeaf,
  fields: FieldLike[],
  resolvedRefs?: Record<string, string>,
): string {
  const field = fields.find((f) => f.slug === leaf.fieldSlug);
  const name = field?.name ?? leaf.fieldSlug ?? 'поле';
  const op = OPERATOR_LABELS[leaf.operator] ?? leaf.operator;
  if (leaf.operator === 'is_empty' || leaf.operator === 'is_not_empty') return `${name} ${op}`;
  return `${name} ${op} ${labelForValue(field, leaf.value, resolvedRefs)}`;
}

/**
 * Весь набор одной строкой. Разделитель — фактический combinator условия
 * (`И` / `ИЛИ`), чтобы сводка не врала при смешанных наборах.
 */
export function describeFilters(
  filters: FilterLeaf[],
  fields: FieldLike[],
  resolvedRefs?: Record<string, string>,
): string {
  if (filters.length === 0) return '';
  return filters
    .map((leaf, i) => {
      const text = describeFilterLeaf(leaf, fields, resolvedRefs);
      if (i === 0) return text;
      return `${leaf.combinator === 'or' ? 'ИЛИ' : 'И'} ${text}`;
    })
    .join(' ');
}
